import React, { useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import SectionCard from "../shared/SectionCard";

const BASE = "#F6F5F7";
const HI_LO = "#C4B5FD";
const HI_MED = "#9E77ED";
const HI_HI = "#7F56D9";
const HI_HOV = "#5B21B6";

// FIPS codes to exclude (Alaska, Hawaii — not part of continental US)
const EXCLUDE_FIPS = new Set(["02", "15"]);

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const FIPS_TO_STATE = {
  "01": "Alabama",
  "02": "Alaska",
  "04": "Arizona",
  "05": "Arkansas",
  "06": "California",
  "08": "Colorado",
  "09": "Connecticut",
  10: "Delaware",
  12: "Florida",
  13: "Georgia",
  15: "Hawaii",
  16: "Idaho",
  17: "Illinois",
  18: "Indiana",
  19: "Iowa",
  20: "Kansas",
  21: "Kentucky",
  22: "Louisiana",
  23: "Maine",
  24: "Maryland",
  25: "Massachusetts",
  26: "Michigan",
  27: "Minnesota",
  28: "Mississippi",
  29: "Missouri",
  30: "Montana",
  31: "Nebraska",
  32: "Nevada",
  33: "New Hampshire",
  34: "New Jersey",
  35: "New Mexico",
  36: "New York",
  37: "North Carolina",
  38: "North Dakota",
  39: "Ohio",
  40: "Oklahoma",
  41: "Oregon",
  42: "Pennsylvania",
  44: "Rhode Island",
  45: "South Carolina",
  46: "South Dakota",
  47: "Tennessee",
  48: "Texas",
  49: "Utah",
  50: "Vermont",
  51: "Virginia",
  53: "Washington",
  54: "West Virginia",
  55: "Wisconsin",
  56: "Wyoming",
};

/**
 * Interactive SVG map of the contiguous 48 US states.
 * States with data highlight on hover; states without data are not interactive.
 */
const USAMap = ({ states = [] }) => {
  const [hoveredState, setHoveredState] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const stateMap = useMemo(() => {
    const m = {};
    (states || []).forEach((s) => {
      m[s.state] = s;
    });
    return m;
  }, [states]);

  const getFill = (name) => {
    const d = stateMap[name];
    if (!d) return BASE;
    if (hoveredState === name) return HI_HOV;
    if (d.pct >= 70) return HI_HI;
    if (d.pct >= 40) return HI_MED;
    return HI_LO;
  };

  const tooltipData = hoveredState ? stateMap[hoveredState] : null;
  const containerW = containerRef.current?.offsetWidth ?? 400;
  const TOOLTIP_W = 148;
  const tipLeft = Math.min(mousePos.x + 14, containerW - TOOLTIP_W - 6);
  const tipTop = Math.max(mousePos.y - 80, 4);

  return (
    <div
      ref={containerRef}
      className="rounded-3"
      style={{ position: "relative" }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }}
    >
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 920 }}
        width={800}
        height={450}
        style={{ width: "100%", height: "auto", display: "block" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies
              .filter(
                (geo) => !EXCLUDE_FIPS.has(String(geo.id).padStart(2, "0")),
              )
              .map((geo) => {
                const fips = String(geo.id).padStart(2, "0");
                const name = FIPS_TO_STATE[fips];
                const hasData = !!stateMap[name];
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getFill(name)}
                    stroke="#E5E4E5"
                    strokeWidth={0.8}
                    onMouseEnter={
                      hasData ? () => setHoveredState(name) : undefined
                    }
                    onMouseLeave={
                      hasData ? () => setHoveredState(null) : undefined
                    }
                    style={{
                      default: {
                        outline: "none",
                        cursor: hasData ? "pointer" : "default",
                        transition: "fill 0.12s",
                      },
                      hover: { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
          }
        </Geographies>
      </ComposableMap>

      {tooltipData && (
        <div
          style={{
            position: "absolute",
            left: tipLeft,
            top: tipTop,
            width: TOOLTIP_W,
            background: "#fff",
            border: "1px solid #E4E7EC",
            borderRadius: 10,
            padding: "10px 14px",
            boxShadow: "0 4px 20px rgba(16,24,40,0.14)",
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <div
            style={{
              fontSize: "0.95rem",
              color: "#101828",
              fontWeight: 700,
              marginBottom: 4,
            }}
          >
            {tooltipData.state}
          </div>
          <div
            style={{ fontSize: "0.85rem", color: "#344054", fontWeight: 500 }}
          >
            {(tooltipData.value ?? 0).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Impressions by State section: interactive USA map + state progress bars.
 */
const ImpressionsByState = ({
  title = "Impressions by State",
  totalLabel = "",
  states = [],
  tooltip = null,
}) => (
  <SectionCard title={title}>
    <div className="row g-3 align-items-start">
      <div className="col-12 col-lg-7">
        <USAMap states={states} />
      </div>

      <div className="col-12 col-lg-5">
        <div
          style={{
            fontSize: "0.875rem",
            color: "#667085",
            fontWeight: "500",
            marginBottom: "0.5rem",
          }}
        >
          Total Impressions
        </div>

        <div
          className="fw-bold mb-3 text-center"
          style={{ fontSize: "2rem", color: "#101828" }}
        >
          {totalLabel}
        </div>

        <div className="d-flex flex-column gap-2">
          {states.map((s) => (
            <div key={s.state}>
              <div className="d-flex justify-content-between mb-1">
                <span style={{ color: "#344054", fontSize: "0.78rem" }}>
                  {s.state}
                </span>
                <span
                  className="fw-semibold"
                  style={{ color: "#101828", fontSize: "0.78rem" }}
                >
                  {(s.value ?? 0).toLocaleString()}
                </span>
              </div>
              <div
                className="rounded-pill"
                style={{ height: 5, background: "#F2F4F7" }}
              >
                <div
                  className="rounded-pill"
                  style={{
                    height: 5,
                    width: `${s.pct ?? 0}%`,
                    background: "linear-gradient(90deg, #7F56D9, #9E77ED)",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </SectionCard>
);

export default ImpressionsByState;
